import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import * as adminMemberRequest from '../../services/adminMemberRequest.service';

const CATEGORY_LABELS = {
  event_request: 'Event request', tech_issue: 'Tech issue', course_request: 'Course request',
  cancel_at_renewal: 'Cancel at renewal', cancel_immediate: 'Cancel now', renew_now: 'Renew now',
  design_request: 'Design request (Canva)', other: 'Other',
};
const STATUSES = ['new', 'in_review', 'scheduled', 'resolved', 'declined'];
const STATUS_LABELS = {
  new: 'New', in_review: 'In review', scheduled: 'Scheduled', resolved: 'Resolved', declined: 'Declined',
};

export default function AdminRequests() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category') || '';
  const isDesignView = categoryFilter === 'design_request';
  const [requests, setRequests] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [nextStatus, setNextStatus] = useState('');
  const [sending, setSending] = useState(false);
  const [composingDesign, setComposingDesign] = useState(false);
  const [designSubject, setDesignSubject] = useState('');
  const [designBody, setDesignBody] = useState('');
  const [submittingDesign, setSubmittingDesign] = useState(false);
  // Topic (Sales & Marketing To-Do 9, 2026-09-29): a short theme Team365 names,
  // so the Sales & Marketing page can show what members ask for. Topics used
  // before are offered again, so the same theme gets the same words.
  const [topicText, setTopicText] = useState('');
  const [savingTopic, setSavingTopic] = useState(false);
  const knownTopics = [...new Set((requests || []).map(r => r.topic).filter(Boolean))].sort();
  const saveTopic = async (r) => {
    setSavingTopic(true);
    try {
      const updated = await adminMemberRequest.setRequestTopic(r.id, topicText);
      setRequests(rs => rs.map(x => (x.id === r.id ? { ...x, topic: updated.topic } : x)));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the topic.');
    } finally {
      setSavingTopic(false);
    }
  };

  const load = useCallback(() => {
    adminMemberRequest.listAllRequests(statusFilter || undefined)
      .then(all => setRequests(categoryFilter ? all.filter(r => r.category === categoryFilter) : all))
      .catch(err => setError(err.response?.data?.error || 'Could not load requests.'));
  }, [statusFilter, categoryFilter]);

  useEffect(() => { setRequests(null); load(); }, [load]);

  const submitDesignRequest = async () => {
    if (!designBody.trim()) return;
    setSubmittingDesign(true);
    try {
      await adminMemberRequest.createDesignRequest({ subject: designSubject.trim() || null, body: designBody.trim() });
      setDesignSubject('');
      setDesignBody('');
      setComposingDesign(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not submit this.');
    } finally {
      setSubmittingDesign(false);
    }
  };

  const toggleExpand = async (r) => {
    if (expandedId === r.id) { setExpandedId(null); setDetail(null); return; }
    setExpandedId(r.id);
    setTopicText(r.topic || '');
    setResponseText('');
    setNextStatus(r.status);
    const data = await adminMemberRequest.getRequestDetail(r.id);
    setDetail(data);
  };

  const submitResponse = async (id) => {
    if (!responseText.trim()) return;
    setSending(true);
    try {
      await adminMemberRequest.respondToRequest(id, { body: responseText.trim(), status: nextStatus });
      setResponseText('');
      const data = await adminMemberRequest.getRequestDetail(id);
      setDetail(data);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send this response.');
    } finally {
      setSending(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        <AdminNav />

        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-serif text-2xl font-bold text-reset-brown">
              {isDesignView ? 'Canva Design Requests' : 'Member Requests'}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {isDesignView
                ? 'What Team365 has asked Claude to build in Canva, and where each one stands.'
                : 'Event requests, tech issues, course requests, cancellation feedback, and design requests — all in one inbox.'}
            </p>
            {isDesignView && (
              <button onClick={() => setSearchParams({})} className="text-xs text-reset-terracotta hover:underline mt-1">
                ← See all Member Requests
              </button>
            )}
          </div>
          {!composingDesign && (
            <button
              onClick={() => setComposingDesign(true)}
              className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors shrink-0"
            >
              🎨 New design request
            </button>
          )}
        </div>

        {composingDesign && (
          <div className="bg-white rounded-2xl shadow-sm p-5 mb-5 space-y-3">
            <div>
              <p className="text-sm font-semibold text-reset-brown">Ask Claude to build a Canva design</p>
              <p className="text-xs text-gray-500 mt-0.5">Uses the 365reset brand kit only — real stock images, never AI-generated. Any Team365 admin can see and follow up on this.</p>
            </div>
            <input
              value={designSubject} onChange={e => setDesignSubject(e.target.value)}
              placeholder="Title (e.g. Sept 1 Writing Sprint event graphic)"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
            <textarea
              value={designBody} onChange={e => setDesignBody(e.target.value)} rows={4}
              placeholder="What do you need? Include the event/post details, date, and any specific text or photo to use."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setComposingDesign(false); setDesignSubject(''); setDesignBody(''); }} className="px-4 py-2 text-sm text-gray-500 hover:text-reset-brown">
                Cancel
              </button>
              <button
                onClick={submitDesignRequest} disabled={submittingDesign || !designBody.trim()}
                className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
              >
                {submittingDesign ? 'Sending…' : 'Submit'}
              </button>
            </div>
          </div>
        )}

        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 text-xs font-medium w-fit mb-5 flex-wrap">
          {[['', 'All'], ...STATUSES.map(s => [s, STATUS_LABELS[s]])].map(([v, l]) => (
            <button
              key={v} onClick={() => setStatusFilter(v)}
              className={`px-3 py-1.5 rounded-md transition-colors ${statusFilter === v ? 'bg-white text-reset-brown shadow-sm' : 'text-gray-500 hover:text-reset-brown'}`}
            >
              {l}
            </button>
          ))}
        </div>

        {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>}

        {!requests ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : requests.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm px-5 py-8 text-center text-sm text-gray-400">
            {isDesignView ? 'No design requests yet — use "New design request" above.' : 'Nothing here.'}
          </div>
        ) : (
          <div className="space-y-2">
            {requests.map(r => (
              <div key={r.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <button onClick={() => toggleExpand(r)} className="w-full text-left p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-reset-cream text-reset-brown">
                        {CATEGORY_LABELS[r.category] || r.category}
                      </span>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                      {r.topic && (
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-reset-sage/15 text-reset-sage">{r.topic}</span>
                      )}
                      <p className="text-sm font-semibold text-reset-brown truncate">{r.subject || r.member_name}</p>
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-2">{r.body}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{r.member_name} · {r.member_email} · {new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                </button>

                {expandedId === r.id && detail && (
                  <div className="border-t border-gray-100 p-4 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <label htmlFor={`topic-${r.id}`} className="text-xs font-medium text-gray-500">Topic</label>
                      <input
                        id={`topic-${r.id}`} list="request-topics" value={topicText} onChange={e => setTopicText(e.target.value)}
                        placeholder="e.g. Sleep help"
                        className="flex-1 min-w-[160px] border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                      />
                      <button
                        type="button" onClick={() => saveTopic(r)} disabled={savingTopic || (topicText.trim() === (r.topic || ''))}
                        className="px-3 py-1.5 border border-reset-terracotta text-reset-terracotta text-xs font-medium rounded-lg hover:bg-reset-cream disabled:opacity-40"
                      >
                        {savingTopic ? 'Saving…' : 'Save topic'}
                      </button>
                      <datalist id="request-topics">{knownTopics.map(t => <option key={t} value={t} />)}</datalist>
                    </div>
                    {detail.responses.map(resp => (
                      <p key={resp.id} className="text-sm text-reset-brown bg-reset-cream rounded-lg p-2.5">{resp.body}</p>
                    ))}
                    <textarea
                      value={responseText} onChange={e => setResponseText(e.target.value)} rows={3}
                      placeholder="Write a response…"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                    />
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <select
                        value={nextStatus} onChange={e => setNextStatus(e.target.value)}
                        className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                      >
                        {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                      </select>
                      <button
                        onClick={() => submitResponse(r.id)} disabled={sending || !responseText.trim()}
                        className="px-4 py-1.5 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
                      >
                        {sending ? 'Sending…' : 'Respond & update status'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
